import React, { useState, useEffect } from 'react';
import api from '../config/api';
import SearchBar from '../components/SearchBar';
import './ManagementPages.css';

const formatStatus = (status) => {
  if (!status) return '';
  return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const CATEGORIES = [
  'laptop', 'monitor', 'keyboard', 'mouse', 'headphones',
  'phone', 'tablet', 'desk', 'chair', 'other'
];

const CONDITIONS = ['new', 'good', 'fair', 'poor', 'damaged'];
const STATUSES = ['available', 'assigned', 'in-repair', 'retired'];

const emptyForm = {
  itemName: '',
  category: 'laptop',
  brand: '',
  model: '',
  serialNumber: '',
  purchaseDate: '',
  purchasePrice: '',
  condition: 'good',
  status: 'available',
  assignedTo: '',
  notes: ''
};

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const [assignModal, setAssignModal] = useState(null); // holds the item being assigned
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');

  const [historyModal, setHistoryModal] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter, search]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/inventory', {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          category: categoryFilter || undefined
        }
      });
      setItems(response.data);
    } catch (err) {
      setError('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/api/employees');
      setEmployees(response.data);
    } catch (err) {
      // Non-fatal — inventory still usable without assignment
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        itemName: item.itemName || '',
        category: item.category || 'other',
        brand: item.brand || '',
        model: item.model || '',
        serialNumber: item.serialNumber || '',
        purchaseDate: item.purchaseDate?.split('T')[0] || '',
        purchasePrice: item.purchasePrice ?? '',
        condition: item.condition || 'good',
        status: item.status || 'available',
        assignedTo: item.assignedTo?._id || item.assignedTo || '',
        notes: item.notes || ''
      });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      purchasePrice: formData.purchasePrice === '' ? 0 : parseFloat(formData.purchasePrice),
      assignedTo: formData.assignedTo || null,
      serialNumber: formData.serialNumber || undefined
    };
    try {
      if (editingItem) {
        await api.put(`/api/inventory/${editingItem._id}`, payload);
      } else {
        await api.post('/api/inventory', payload);
      }
      fetchItems();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save inventory item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory item?')) return;
    try {
      await api.delete(`/api/inventory/${id}`);
      fetchItems();
    } catch (err) {
      setError('Failed to delete inventory item');
    }
  };

  const openAssignModal = (item) => {
    setAssignModal(item);
    setAssignEmployeeId('');
    setAssignNotes('');
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignEmployeeId) return;
    try {
      await api.post(`/api/inventory/${assignModal._id}/assign`, {
        employeeId: assignEmployeeId,
        notes: assignNotes || undefined
      });
      setAssignModal(null);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign item');
    }
  };

  const handleUnassign = async (id) => {
    if (!window.confirm('Mark this item as returned?')) return;
    try {
      await api.post(`/api/inventory/${id}/unassign`);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unassign item');
    }
  };

  const openHistory = async (item) => {
    try {
      const response = await api.get(`/api/inventory/${item._id}`);
      setHistoryModal(response.data);
    } catch (err) {
      setError('Failed to load assignment history');
    }
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Inventory Management</h1>
        <button className="btn btn-primary" onClick={() => openModal()}>
          + Add Item
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="filters">
        <SearchBar
          placeholder="Search by name, brand, model, serial..."
          onSearch={setSearch}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => (
            <option key={c} value={c}>{formatStatus(c)}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>{formatStatus(s)}</option>
          ))}
        </select>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Category</th>
              <th>Brand / Model</th>
              <th>Serial #</th>
              <th>Condition</th>
              <th>Status</th>
              <th>Assigned To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: '#aaa' }}>No inventory items yet.</td></tr>
            )}
            {items.map(item => (
              <tr key={item._id}>
                <td>{item.itemName}</td>
                <td>{formatStatus(item.category)}</td>
                <td>{[item.brand, item.model].filter(Boolean).join(' ') || '—'}</td>
                <td>{item.serialNumber || '—'}</td>
                <td>{formatStatus(item.condition)}</td>
                <td>
                  <span className={`status-badge status-${item.status}`}>
                    {formatStatus(item.status)}
                  </span>
                </td>
                <td>
                  {item.assignedTo ? (
                    <div>
                      <div>{item.assignedTo.name}</div>
                      <div style={{ fontSize: '11px', color: '#aaa' }}>
                        {item.assignedDate ? new Date(item.assignedDate).toLocaleDateString() : ''}
                      </div>
                    </div>
                  ) : (
                    <span style={{ color: '#888' }}>—</span>
                  )}
                </td>
                <td>
                  {item.assignedTo ? (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleUnassign(item._id)}
                      style={{ fontSize: '11px', padding: '4px 8px', marginRight: '4px' }}
                      title="Return / Unassign"
                    >
                      Return
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      onClick={() => openAssignModal(item)}
                      style={{ fontSize: '11px', padding: '4px 8px', marginRight: '4px', backgroundColor: '#1a7a4a', borderColor: '#1a7a4a' }}
                      title="Assign to employee"
                    >
                      Assign
                    </button>
                  )}
                  <button
                    className="btn-icon"
                    onClick={() => openHistory(item)}
                    title="History"
                    style={{ fontSize: '14px' }}
                  >
                    📜
                  </button>
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => openModal(item)}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(item._id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Inventory Item' : 'Add Inventory Item'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    name="itemName"
                    value={formData.itemName}
                    onChange={handleChange}
                    required
                    placeholder="e.g. MacBook Pro 14"
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select name="category" value={formData.category} onChange={handleChange} required>
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{formatStatus(c)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Brand</label>
                  <input
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder="e.g. Apple"
                  />
                </div>
                <div className="form-group">
                  <label>Model</label>
                  <input
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="e.g. M3 Pro"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Serial Number</label>
                  <input
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Purchase Date</label>
                  <input
                    type="date"
                    name="purchaseDate"
                    value={formData.purchaseDate}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Purchase Price (PKR)</label>
                  <input
                    type="number"
                    name="purchasePrice"
                    value={formData.purchasePrice}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Condition</label>
                  <select name="condition" value={formData.condition} onChange={handleChange}>
                    {CONDITIONS.map(c => (
                      <option key={c} value={c}>{formatStatus(c)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    {STATUSES.map(s => (
                      <option key={s} value={s}>{formatStatus(s)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
                    <option value="">— Unassigned —</option>
                    {employees.map(emp => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name} {emp.department ? `(${emp.department})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {assignModal && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>Assign Item</h2>
              <button className="close-btn" onClick={() => setAssignModal(null)}>×</button>
            </div>

            <div style={{ marginBottom: '16px', padding: '10px', background: '#1e1e2e', borderRadius: '6px' }}>
              <div style={{ fontWeight: 'bold' }}>{assignModal.itemName}</div>
              <div style={{ fontSize: '12px', color: '#aaa' }}>
                {[assignModal.brand, assignModal.model].filter(Boolean).join(' ')}
                {assignModal.serialNumber ? ` · SN: ${assignModal.serialNumber}` : ''}
              </div>
            </div>

            <form onSubmit={handleAssign}>
              <div className="form-group">
                <label>Employee *</label>
                <select
                  value={assignEmployeeId}
                  onChange={(e) => setAssignEmployeeId(e.target.value)}
                  required
                >
                  <option value="">Select an employee</option>
                  {employees.map(emp => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} {emp.department ? `(${emp.department})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={assignNotes}
                  onChange={(e) => setAssignNotes(e.target.value)}
                  rows="3"
                  placeholder="e.g. Issued with charger and case"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setAssignModal(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Assign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historyModal && (
        <div className="modal-overlay" onClick={() => setHistoryModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Assignment History</h2>
              <button className="close-btn" onClick={() => setHistoryModal(null)}>×</button>
            </div>

            <div style={{ marginBottom: '16px', padding: '10px', background: '#1e1e2e', borderRadius: '6px' }}>
              <div style={{ fontWeight: 'bold' }}>{historyModal.itemName}</div>
              <div style={{ fontSize: '12px', color: '#aaa' }}>
                {[historyModal.brand, historyModal.model].filter(Boolean).join(' ')}
                {historyModal.serialNumber ? ` · SN: ${historyModal.serialNumber}` : ''}
              </div>
            </div>

            {(!historyModal.assignmentHistory || historyModal.assignmentHistory.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#aaa' }}>
                No assignment history yet.
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {[...historyModal.assignmentHistory].reverse().map((entry, idx) => (
                  <div key={idx} style={{ border: '1px solid #333', borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {entry.employee?.name || entry.employeeName || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                      Assigned: {entry.assignedDate ? new Date(entry.assignedDate).toLocaleString() : '—'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#aaa' }}>
                      Returned: {entry.returnedDate ? new Date(entry.returnedDate).toLocaleString() : <span style={{ color: '#4caf50' }}>Currently assigned</span>}
                    </div>
                    {entry.notes && (
                      <div style={{ fontSize: '12px', color: '#ccc', marginTop: '6px', fontStyle: 'italic' }}>
                        {entry.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
